import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { User } from '../_models/User';
import { ClientType } from '../_models/ClientType';
import { Engine } from '../_models/Engine';
import { Role } from '../_models/Role';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';


@Injectable()
export class UserService implements OnDestroy {
    userToken: any;
    decodedToken: any;
    userPermissions: { [name: string]: boolean; } = {};

    constructor(private http: HttpClient, private jwtTokenService: JWTTokenService) { }

    ngOnDestroy() {
        this.userPermissions = {};
    }

    public createUser(model: any) {
        return this.http.post(getUrl('user/create'), model, this.requestOptionsAuthenticated());
    }

    public createClient(model: any) : Observable<any> {
        return this.http.post<any>(getUrl('user/client/create'), model, this.requestOptionsAuthenticated());
    }

    public updateUser(model: any) {
        return this.http.put(getUrl('user/'), model, this.requestOptionsAuthenticated());
    }

    public acceptCurrentUserTos(value: boolean) {
        return this.http.put(getUrl('user/acceptTos'), value, this.requestOptionsAuthenticated());
    }

    public getUser(userId: number): Observable<User> {
        return this.http.get<User>(getUrl('user/' + userId), this.requestOptionsAuthenticated());
    }

    public getAllUsers(): Observable<User[]> {
        return this.http.get<User[]>(getUrl('user'), this.requestOptionsAuthenticated());
    }

    public deleteUser(userId: number) {
        return this.http.get(getUrl('user/delete/' + userId), this.requestOptionsAuthenticated());
    }

    public enableUser(userId: number) {
        return this.http.get(getUrl('user/enable/' + userId), this.requestOptionsAuthenticated());
    }

    public getClientTypes(organizationId: number): Observable<ClientType[]> {
        return this.http.get<ClientType[]>(getUrl('user/client-type/organization/' + organizationId), this.requestOptionsAuthenticated());
    }

    public getCUEFundraisingClientTypes(): Observable<ClientType[]> {
        return this.http.get<ClientType[]>(getUrl('user/client-type/fundraising/'), this.requestOptionsAuthenticated());
    }

    public getClientType(clientTypeId: number): Observable<ClientType> {
        return this.http.get<ClientType>(getUrl('user/client-type/' + clientTypeId), this.requestOptionsAuthenticated());
    }

    public getAllClientTypes(): Observable<ClientType[]> {
        return this.http.get<ClientType[]>(getUrl('user/client-type/'), this.requestOptionsAuthenticated());
    }

    public getUserEngines(): Observable<Engine[]> {
        return this.http.get<Engine[]>(getUrl('user/engine'), this.requestOptionsAuthenticated());
    }

    public checkUserSubscription() {
        return this.http.get<any>(getUrl('user/subscription'), this.requestOptionsAuthenticated());
    }

    public hasPermissions(permissions: string[]): Observable<{ [name: string]: boolean; }> {
        return this.http.post<{ [name: string]: boolean; }>(getUrl('user/check/'), permissions, this.requestOptionsAuthenticated());
    }

    public hasSinglePermission(permission: string): Observable<boolean> {
        if(this.userPermissions[permission] != undefined) {
            return of(this.userPermissions[permission] ?? false);
        } else {
            return this.http.get<boolean>(getUrl('user/check/' + permission), this.requestOptionsAuthenticated())
            .pipe(map(response => {
                this.userPermissions[permission] = response;
                return this.userPermissions[permission] ?? false; 
            }));
        }
    }


    public getRoles(): Observable<Role[]> {
        return this.http.get<Role[]>(getUrl('auth/roles'), this.requestOptionsAuthenticated());
    }

    public createClientType(newClientType: any) {
        return this.http.post(getUrl('user/client-type/create'), newClientType, this.requestOptionsAuthenticated());
    }

    public resendIntroductionEmail(userId: number) {
        return this.http.get<any>(getUrl('user/resend-introduction/' + userId), this.requestOptionsAuthenticated());
    }

    public exportUserData() {
        return this.http.get(getUrl('user/export/'), this.requestOptionsAuthenticated());
    }

    private requestOptionsAuthenticated() {
        const requestOptions = {
            headers: new HttpHeaders()
                .set('Content-type', 'application/json')
                .append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal')),
        };
        return requestOptions;
    }
}
